<template>
  <div class="guide-page mx-auto mt-2 max-w-[1280px] md:mt-0">
    <template v-if="guideData && !accessRecoveryFailed">
      <GuideProfileHero :guide="guideData" />

      <div
        class="relative mt-4"
        :class="{
          'lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:items-start lg:gap-4':
            isAffiliate,
        }"
        data-test="guide-profile-layout"
      >
        <aside
          v-if="isAffiliate"
          class="lg:sticky lg:top-4"
          :class="{ 'hidden lg:block': isAffiliate }"
          data-test="guide-profile-primary-navigation"
        >
          <GuideNavigation
            :items="navigationItems"
            :active-id="activeTab"
            :aria-label="$t('pages.guide.profileNavigationLabel')"
            base-id="guide-profile"
            @select="selectTab"
          />
        </aside>

        <section
          ref="profileContent"
          :aria-label="$t('pages.guide.profileContentLabel')"
          class="relative mt-4 rounded-base border border-light-200 bg-white p-5 shadow-sm sm:p-7 lg:mt-0 lg:p-9 dark:border-grey-dark dark:bg-black"
        >
          <div
            v-show="activeTab === 'about'"
            id="guide-profile-about-panel"
            :role="isAffiliate ? 'tabpanel' : undefined"
            :aria-labelledby="
              isAffiliate
                ? 'guide-profile-about-tab'
                : 'guide-profile-about-content-heading'
            "
            tabindex="0"
            class="focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black dark:focus-visible:outline-gold"
          >
            <GuideAboutPanel
              :guide="guideData"
              panel-id="guide-profile-about-content"
            />
          </div>

          <div
            v-if="isAffiliate && validGoogleMapsLink"
            v-show="activeTab === 'map'"
            id="guide-profile-map-panel"
            role="tabpanel"
            aria-labelledby="guide-profile-map-tab"
            tabindex="0"
            class="focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black dark:focus-visible:outline-gold"
          >
            <GuideMapPanel
              v-if="hasOpenedMap"
              :guide="guideData"
              panel-id="guide-profile-map-content"
            />
          </div>

          <div
            v-if="isAffiliate && hasRecommendations"
            v-show="activeTab === 'recommendations'"
            id="guide-profile-recommendations-panel"
            role="tabpanel"
            aria-labelledby="guide-profile-recommendations-tab"
            tabindex="0"
            class="focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black dark:focus-visible:outline-gold"
          >
            <GuideRecommendationsPanel
              v-if="hasOpenedRecommendations"
              :guide="guideData"
              panel-id="guide-profile-recommendations-content"
              :is-active="activeTab === 'recommendations'"
            />
          </div>

          <div
            v-if="isAffiliate && tourGalleries.length"
            v-show="activeTab === 'gallery'"
            id="guide-profile-gallery-panel"
            role="tabpanel"
            aria-labelledby="guide-profile-gallery-tab"
            tabindex="0"
            class="focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black dark:focus-visible:outline-gold"
          >
            <GuideGalleryPanel
              v-if="hasOpenedGallery"
              :guide="guideData"
              :galleries="tourGalleries"
              :is-active="activeTab === 'gallery'"
              panel-id="guide-profile-gallery-content"
            />
          </div>

          <div
            v-if="isAffiliate"
            v-show="activeTab === 'tours'"
            id="guide-profile-tours-panel"
            role="tabpanel"
            aria-labelledby="guide-profile-tours-tab"
            tabindex="0"
            class="focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-black dark:focus-visible:outline-gold"
          >
            <GuideBookToursPanel
              v-if="hasOpenedTours"
              :city-id="selectedGuideCityId"
              :cities="guideCities"
              panel-id="guide-profile-tours-content"
              @select-city="selectedGuideCityId = $event"
            />
          </div>

          <div
            v-if="tips.enabled.value"
            v-show="activeTab === 'tip'"
            id="guide-profile-tip-panel"
            role="tabpanel"
            aria-labelledby="guide-profile-tip-tab"
          >
            <GuideTipForm
              v-model:currency="tips.currency.value"
              v-model:amount-text="tips.amountText.value"
              :currencies="tips.currencies.value"
              :pending="tips.pending.value"
              :can-pay="tips.canPay.value"
              :valid-amount="tips.validAmount.value"
              :error="tips.error.value"
              :status-unavailable="tips.statusUnavailable.value"
              :refreshing="tips.refreshing.value"
              @pay="tips.checkout"
              @refresh="tips.refresh"
            />
          </div>
        </section>
      </div>
    </template>

    <div
      v-else-if="pending"
      class="space-y-4 motion-safe:animate-pulse"
      aria-busy="true"
      :aria-label="$t('pages.guide.loading')"
    >
      <div
        class="h-[520px] rounded-base bg-light-200 lg:h-80 dark:bg-dark"
      ></div>
      <div class="h-16 rounded-base bg-light-200 dark:bg-dark"></div>
      <div class="h-96 rounded-base bg-light-100 dark:bg-dark"></div>
    </div>

    <GuideEmptyState
      v-else-if="error"
      icon="x-close"
      :title="$t('pages.guide.loadErrorTitle')"
      :description="$t('pages.guide.loadErrorDescription')"
      role="alert"
    >
      <button
        type="button"
        class="mt-5 min-h-11 cursor-pointer rounded-md bg-black px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-gold hover:text-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black dark:bg-white dark:text-black dark:focus-visible:outline-gold"
        @click="refreshGuideDataFromCookies()"
      >
        {{ $t("pages.guide.retry") }}
      </button>
    </GuideEmptyState>

    <GuideEmptyState
      v-else
      icon="smile"
      :title="$t('pages.guide.loadErrorTitle')"
      :description="$t('pages.guide.loadErrorDescription')"
    />

    <TourCarousel
      v-if="guideData && !accessRecoveryFailed && cityProducts.length"
      class="mt-12"
      :title="$t('pages.guide.cityTours', { city: selectedCityName })"
      :tours="cityProducts"
      :is-city-specific="true"
    />
  </div>
</template>

<script lang="ts" setup>
import { useCityStore } from "@/stores/useCityStore";
import { useGuidesApi } from "@/composables/useGuidesApi";
import { useTourProductsApi } from "@/composables/useTourProductsApi";
import { withoutAffiliateQueryParams } from "@/composables/useAffiliateAttribution";
import { useMobileStickyBarContext } from "@/composables/useMobileStickyBarContext";
import { getValidGoogleMapsUrl } from "@/utils/googleMaps";
import { normalizeGuideMaterials } from "@/utils/normalizeGuideBio";
import { useGuideTips } from "@/composables/useGuideTips";
import GuideTipForm from "@/components/guide/GuideTipForm.vue";
import type {
  Guide,
  GuideBookCity,
  GuideGalleryGroup,
  GuideMaterial,
  GuideNavigationItem,
  TourProduct,
} from "@/types";

type GuideTabId =
  | "about"
  | "map"
  | "recommendations"
  | "gallery"
  | "tip"
  | "tours";

const tabHashes: Record<GuideTabId, string> = {
  tip: "#leave-a-tip",
  about: "#about",
  map: "#map",
  recommendations: "#recommendations",
  gallery: "#tours-gallery",
  tours: "#book-a-tour",
};

const route = useRoute();
// Slug-scoped cookies, QR access and mobile menu ownership must never carry
// over when Nuxt navigates between two guide profiles in the same SPA.
definePageMeta({ key: (route) => route.path });
const router = useRouter();
const cityStore = useCityStore();
const config = useRuntimeConfig();
const { t, locale, locales, defaultLocale } = useI18n();
const { getGuideBySlug } = useGuidesApi();
const { getProductsByCity } = useTourProductsApi();

const activeTab = ref<GuideTabId>("about");
const selectedGuideCityId = ref<number | null>(null);
const cityProducts = ref<TourProduct[]>([]);
const selectedCityName = computed(
  () =>
    guideCities.value.find((city) => city.id === selectedGuideCityId.value)
      ?.name ??
    guideData.value?.city ??
    "",
);
const hasOpenedMap = ref(false);
const hasOpenedRecommendations = ref(false);
const hasOpenedGallery = ref(false);
const hasOpenedTours = ref(false);
const isMounted = ref(false);
const profileContent = ref<HTMLElement | null>(null);
const { menu: mobileStickyBarMenu, selection: mobileStickyBarSelection } =
  useMobileStickyBarContext();
const mobileStickyBarOwnerId = `guide-profile:${String(route.params.name)}`;

const hasAffiliateQuery = Boolean(
  route.query.affiliate_code ||
    route.query.affiliate_code_expiration ||
    route.query.guide_access_token,
);
const guideCookieKey = `affiliate_code_${route.params.name}`;
const guideAccessTokenCookieKey = `guide_access_token_${route.params.name}`;
const affiliateCookie = useCookie(guideCookieKey);
const guideAccessTokenCookie = useCookie(guideAccessTokenCookieKey);
const { captureAffiliateAttribution } = useAffiliateAttribution();
const capturedAttribution = captureAffiliateAttribution(route.query);
const validAffiliateCodeFromUrl = capturedAttribution?.code;
const guideAccessTokenFromUrl = capturedAttribution?.guideAccessToken;
let useCapturedAccess = true;

if (capturedAttribution) {
  const affiliateCookieToSet = useCookie(guideCookieKey, {
    maxAge: capturedAttribution.maxAge,
    path: "/",
    sameSite: "lax" as const,
    secure: !import.meta.dev,
  });
  affiliateCookieToSet.value = capturedAttribution.code;

  if (capturedAttribution.guideAccessToken) {
    const guideAccessTokenCookie = useCookie(guideAccessTokenCookieKey, {
      maxAge: capturedAttribution.maxAge,
      path: "/",
      sameSite: "lax" as const,
      secure: !import.meta.dev,
    });
    guideAccessTokenCookie.value = capturedAttribution.guideAccessToken;
  }
}

const {
  data: guideData,
  pending,
  error,
  refresh: refreshGuideData,
} = await useAsyncData<Guide | undefined>(
  `guideData-${route.params.name}-${locale.value}`,
  async () => {
    const slug = route.params.name as string;
    return useCapturedAccess
      ? await getGuideBySlug(
          slug,
          validAffiliateCodeFromUrl,
          guideAccessTokenFromUrl,
        )
      : await getGuideBySlug(slug);
  },
  {
    lazy: true,
    watch: [locale],
  },
);

const isAffiliate = computed(
  () =>
    guideData.value?.access_mode === "affiliate" ||
    (guideData.value?.access_mode === undefined &&
      (guideData.value?.tour_materials !== undefined ||
        guideData.value?.gallery_images !== undefined)),
);

const hasRecommendations = computed(
  () =>
    Boolean(guideData.value?.recommendations?.length) ||
    Boolean(guideData.value?.city_recommendations?.length),
);

const tips = useGuideTips(
  computed(() => String(route.params.name)),
  isAffiliate,
  config.public.tipsEnabled === true,
  config.public.tipsLivemode === true,
  computed(() => activeTab.value === "tip"),
);

const validGoogleMapsLink = computed(() =>
  getValidGoogleMapsUrl(guideData.value?.google_maps_link),
);

const tourGalleries = computed<GuideGalleryGroup[]>(() => {
  const galleries = new Map<number, GuideMaterial[]>();

  for (const item of guideData.value
    ? normalizeGuideMaterials(guideData.value)
    : []) {
    const gallery = galleries.get(item.product_id) ?? [];
    gallery.push(item);
    galleries.set(item.product_id, gallery);
  }

  return Array.from(galleries, ([product_id, items]) => ({
    product_id,
    items: [...items].sort(
      (first, second) =>
        first.order - second.order ||
        String(first.id).localeCompare(String(second.id)),
    ),
  }));
});

const navigationItems = computed<GuideNavigationItem[]>(() => {
  const items: GuideNavigationItem[] = [
    {
      id: "about",
      label: t("pages.guide.about", {
        name: guideData.value?.first_name,
      }),
      icon: "smile",
    },
  ];

  if (isAffiliate.value && validGoogleMapsLink.value) {
    items.push({
      id: "map",
      label: t("pages.guide.recommendationsMap"),
      icon: "marker-pin",
    });
  }
  if (isAffiliate.value && hasRecommendations.value) {
    items.push({
      id: "recommendations",
      label: t("pages.guide.recommendations"),
      icon: "bulb",
    });
  }
  if (isAffiliate.value && tourGalleries.value.length) {
    items.push({
      id: "gallery",
      label: t("pages.guide.toursGallery"),
      icon: "layout-left",
    });
  }
  if (isAffiliate.value) {
    items.push({
      id: "tours",
      label: t("pages.guide.tours"),
      icon: "calendar",
    });
  }

  if (isAffiliate.value && tips.enabled.value) {
    items.push({
      id: "tip",
      label: t("pages.guide.tips.title"),
      icon: "heart",
    });
  }
  return items;
});

const guideCities = computed<GuideBookCity[]>(() => {
  if (guideData.value?.cities?.length) {
    return guideData.value.cities;
  }

  const cities = new Map<number, GuideBookCity>();
  for (const product of guideData.value?.products ?? []) {
    if (product.city?.id && !cities.has(product.city.id)) {
      cities.set(product.city.id, {
        id: product.city.id,
        name: product.city.name,
      });
    }
  }
  return Array.from(cities.values());
});

watch(
  guideCities,
  (cities) => {
    if (!cities.some((city) => city.id === selectedGuideCityId.value)) {
      const primaryCityId = guideData.value?.primary_city_id;
      selectedGuideCityId.value =
        cities.find((city) => city.id === primaryCityId)?.id ??
        cities[0]?.id ??
        null;
    }
  },
  { immediate: true },
);

watch(
  selectedGuideCityId,
  async (cityId, _previousCityId, onCleanup) => {
    let stale = false;
    onCleanup(() => {
      stale = true;
    });
    cityProducts.value = [];
    if (!cityId) return;
    const city = cityStore.cities.find((item) => item.id === cityId);
    if (city?.toursList?.length) {
      cityProducts.value = city.toursList;
      return;
    }
    try {
      const tours = await getProductsByCity(cityId);
      if (stale) return;
      cityProducts.value = tours;
      cityStore.setDataForCity(cityId, { tours });
    } catch (error) {
      if (!stale) console.warn("Unable to load guide city products", error);
    }
  },
  { immediate: true },
);

const selectTabFromHash = (hash: string) => {
  const hashTab = Object.entries(tabHashes).find(
    ([, tabHash]) => tabHash === hash,
  )?.[0] as GuideTabId | undefined;
  if (hashTab) {
    activeTab.value = navigationItems.value.some((item) => item.id === hashTab)
      ? hashTab
      : "about";
  }
};

watch(
  navigationItems,
  (items) => {
    if (isMounted.value) {
      selectTabFromHash(route.hash);
    }
    if (!items.some((item) => item.id === activeTab.value)) {
      activeTab.value = "about";
    }
  },
  { immediate: true },
);

watch(
  () => route.hash,
  (hash) => {
    if (isMounted.value) {
      selectTabFromHash(hash);
    }
  },
);

watch(
  activeTab,
  (tab) => {
    if (tab === "map") {
      hasOpenedMap.value = true;
    } else if (tab === "recommendations") {
      hasOpenedRecommendations.value = true;
    } else if (tab === "gallery") {
      hasOpenedGallery.value = true;
    } else if (tab === "tours") {
      hasOpenedTours.value = true;
    }
  },
  { immediate: true },
);

const selectTab = (id: string) => {
  if (navigationItems.value.some((item) => item.id === id)) {
    activeTab.value = id as GuideTabId;
    const hash = tabHashes[id as GuideTabId];
    if (route.hash !== hash) {
      void router.replace({ query: route.query, hash });
    }
  }
};

const selectTabFromMobileBar = (id: string) => {
  selectTab(id);
  void nextTick(() => {
    if (!profileContent.value) return;
    const contentTop =
      window.scrollY + profileContent.value.getBoundingClientRect().top;
    window.scrollTo({
      top: Math.max(0, contentTop - 16),
      behavior: "smooth",
    });
  });
};

watchEffect(() => {
  if (!isMounted.value) return;

  if (!isAffiliate.value) {
    if (mobileStickyBarMenu.value?.ownerId === mobileStickyBarOwnerId) {
      mobileStickyBarMenu.value = null;
    }
    return;
  }

  mobileStickyBarMenu.value = {
    ownerId: mobileStickyBarOwnerId,
    label: t("mobileStickyBar.sections"),
    ariaLabel: t("mobileStickyBar.guideSections"),
    items: navigationItems.value,
    activeId: activeTab.value,
  };
});

watch(mobileStickyBarSelection, (selection) => {
  if (!selection || selection.ownerId !== mobileStickyBarOwnerId) return;
  selectTabFromMobileBar(selection.id);
  mobileStickyBarSelection.value = null;
});

const metaTitle = computed(
  () =>
    guideData.value?.seo_title ||
    (guideData.value
      ? t("pages.guide.metaTitle", {
          name: guideData.value.first_name,
          city: guideData.value.city,
        })
      : t("pages.guides.seo_title")),
);
const metaDescription = computed(
  () =>
    guideData.value?.seo_meta_description ||
    t("pages.guides.seo_meta_description"),
);

watchEffect(() => {
  useHead({
    title: metaTitle.value,
    meta: [{ name: "description", content: metaDescription.value }],
    link: guideData.value?.path
      ? generateSEOLinks(
          locales.value,
          defaultLocale,
          locale.value,
          config.public.websiteUrl,
          [
            {
              en: "guides",
              es: "guides",
              de: "guides",
              pl: "guides",
            },
            {
              en: guideData.value.path,
              es: guideData.value.path,
              de: guideData.value.path,
              pl: guideData.value.path,
            },
          ],
        )
      : [],
  });
});

watch(
  error,
  (loadError) => {
    if (!loadError || typeof loadError !== "object") return;
    const candidate = loadError as {
      status?: number;
      statusCode?: number;
      response?: { status?: number };
    };
    const statusCode =
      candidate.statusCode || candidate.status || candidate.response?.status;
    if (statusCode === 404) {
      throw showError({ statusCode: 404 });
    }
  },
  { immediate: true },
);

watch(
  guideData,
  (data) => {
    if (data === null || data === undefined) return;
    if (!data.id) {
      throw showError({ statusCode: 404 });
    }
    if (
      data.is_hidden &&
      !validAffiliateCodeFromUrl &&
      !affiliateCookie.value
    ) {
      navigateTo("/guides/");
    }
  },
  { immediate: true },
);

const hasGuideAccessCookie = () =>
  Boolean(affiliateCookie.value || guideAccessTokenCookie.value);

// A suspended tab can miss a cookie change made by the QR redirect in another
// tab. Read the browser's current cookies when the tab becomes visible again.
const hasStoredGuideAccessCookie = () => {
  if (!import.meta.client) return hasGuideAccessCookie();
  return document.cookie.split(";").some((cookie) => {
    const separator = cookie.indexOf("=");
    if (separator < 0 || separator === cookie.length - 1) return false;
    const name = cookie.slice(0, separator).trim();
    return name === guideCookieKey || name === guideAccessTokenCookieKey;
  });
};

const accessRecoveryFailed = computed(
  () => Boolean(error.value && !isAffiliate.value && hasStoredGuideAccessCookie()),
);

let accessRefresh: Promise<void> | null = null;
const refreshGuideDataFromCookies = () => {
  if (accessRefresh) return accessRefresh;
  useCapturedAccess = false;
  accessRefresh = refreshGuideData().finally(() => {
    accessRefresh = null;
  });
  return accessRefresh;
};

const handlePageShow = (event: PageTransitionEvent) => {
  if (
    event.persisted &&
    (hasStoredGuideAccessCookie() || hasGuideAccessCookie() || isAffiliate.value)
  ) {
    void refreshGuideDataFromCookies();
  }
};

const refreshChangedAccess = () => {
  const hasAccessCookie = hasStoredGuideAccessCookie();
  if (hasAccessCookie !== isAffiliate.value) {
    void refreshGuideDataFromCookies();
  }
};

const handleVisibilityChange = () => {
  if (document.visibilityState === "visible") refreshChangedAccess();
};

onMounted(async () => {
  isMounted.value = true;
  selectTabFromHash(route.hash);
  window.addEventListener("pageshow", handlePageShow);
  window.addEventListener("focus", refreshChangedAccess);
  window.addEventListener("online", refreshChangedAccess);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  document.addEventListener("resume", refreshChangedAccess);

  if (hasGuideAccessCookie() || hasStoredGuideAccessCookie() || isAffiliate.value) {
    await refreshGuideDataFromCookies();
  }

  if (hasAffiliateQuery) {
    await router.replace({
      query: withoutAffiliateQueryParams(route.query),
      hash: route.hash,
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("pageshow", handlePageShow);
  window.removeEventListener("focus", refreshChangedAccess);
  window.removeEventListener("online", refreshChangedAccess);
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  document.removeEventListener("resume", refreshChangedAccess);
  if (mobileStickyBarMenu.value?.ownerId === mobileStickyBarOwnerId) {
    mobileStickyBarMenu.value = null;
  }
  if (mobileStickyBarSelection.value?.ownerId === mobileStickyBarOwnerId) {
    mobileStickyBarSelection.value = null;
  }
});
</script>
